import { AsButton, useModal } from '@adserve/adserve-react-components';
import AsSelectWithPlaceholder from '../../generic/AsSelectWithPlaceholder';
import React, { useEffect, useState, useContext } from 'react';
import {
    goalsContainerStyle,
    planButtonStyle,
    planManagerContainerStyle,
    reservedTextStyle,
    disabledPlanButtonStyle,
    airtimeReservationTextStyle,
    campaignGoalTitleStyle,
    disabledStyle
} from './AirtimePlanningStyles';
import { ReactComponent as IconChevronDown } from '../../../svg/icon_chevron_down.svg';
import PlanGoals from './PlanGoals';
import ReservationTimer from '../../generic/ReservationTimer';
import DeliveryTableModal from './ModalDelivery/DeliveryTableModal';
import DeliveryTableColumnDefinitions from './ModalDelivery/DeliveryTableColumnDefinitions';
import { BusinessTypes, CampaignGoalEnum } from '../../../constants/Enums';
import {
    GetActiveQuotes,
    GetCampaignBurst,
    GetCampaignSettings,
    GetPlanStations
} from '../../../services/CampaignsDataService';
import { SystemSettingsContext } from '../../../contexts/SystemSettingsContext';

let mounted = false;

const PlanManager = ({
    plan,
    selectedBurstId,
    readOnly = false,
    reservedUntil,
    goals,
    updateGoal,
    hasPlan,
    planning,
    setReady,
    campaign,
    setErrorState,
    canQuote,
    selectedStationId,
    setSelectedStationId,
    selectedPlanId,
    showImpacts,
    showBaseImpacts,
    stationChange,
    planExpired,
    setPlanExpired,
    lastPlanIsBooked,
    totalAirtimeCost,
    renderHack
}) => {
    const { formatCurrency } = useContext(SystemSettingsContext);
    const [goalsMenuOpen, setGoalsMenuOpen] = useState(false);
    const [isReserved, setIsReserved] = useState(true);
    const [currentGoalLabel, setCurrentGoalLabel] = useState('');
    const [stations, setStations] = useState([]);
    const [runningNewPlan, setRunningNewPlan] = useState(false);
    const [goalIndex, setGoalIndex] = useState();
    const [modalShowing, setModalShowing] = useState(false);
    const [isPackage, setIsPackage] = useState(false);
    const [multiplePlanSelection, setMultiplePlanSelection] = useState(false);
    const [selectedBurst, setSelectedBurst] = useState();
    const { addMessage } = useModal();

    const whiteButtonStyle = {
        border: '2px solid #CCCCCC',
        backgroundColor: '#FFFFFF',
        cursor: 'pointer'
    };

    const handleClose = () => {
        if (!mounted) {
            return;
        }
        setModalShowing(false);
    };

    const handleOpen = () => {
        if (!mounted) {
            return;
        }
        setModalShowing(true);
    };

    useEffect(async () => {
        let stationList = [];

        if (selectedBurst?.burstStations) {
            stationList = selectedBurst.burstStations
                ?.filter((csf) => csf.budget > 0 || csf.cpt > 0)
                ?.map((cs) => {
                    return { id: cs.id, name: cs.name };
                });
        } else {
            if (plan?.id) {
                stationList = (await GetPlanStations(plan.id)).data;
            } else {
                stationList = campaign?.campaignStations
                    ?.filter((csf) => csf.budget > 0 || csf.cpt > 0)
                    ?.map((cs) => {
                        return { id: cs.id, name: cs.name };
                    });
            }
        }
        setStations(stationList.sort((s1, s2) => (s1.name.toLowerCase() > s2.name.toLowerCase() ? 1 : -1)));
    }, [campaign.campaignStations, selectedBurst?.burstStations]);

    useEffect(() => {
        if (plan && mounted) {
            plan.deliveryMode && totalAirtimeCost
                ? setCurrentGoalLabel(
                      `Delivery Mode - ${plan.deliveryMode} - Total Airtime Cost: ${formatCurrency(totalAirtimeCost)}`
                  )
                : setCurrentGoalLabel('');
            setRunningNewPlan(false);
        }
    }, [plan, totalAirtimeCost]);

    const goPlan = (v) => {
        if (!mounted) {
            return;
        }
        setGoalsMenuOpen(false);
        setRunningNewPlan(true);
        setGoalIndex(v);
    };
    const planPackage = () => {
        if (!mounted) {
            return;
        }

        // If the selected goal is 0 then we don't plan
        const selectedGoal = selectedBurst
            ? selectedBurst.campaignGoal.find((g) => g.selected)
            : campaign.campaignGoal.find((g) => g.selected);
        if (!selectedGoal) {
            setErrorState(window.translate('No goal selected'));
            return;
        }

        let value = parseFloat(selectedGoal.value);

        if (selectedGoal.goal === CampaignGoalEnum.WEEKLYBUDGET) {
            value = selectedGoal?.values?.reduce((a, b) => {
                return a + parseFloat(b.value);
            }, 0);
        }

        if (isNaN(value)) {
            value = 0;
        }

        if (value === 0) {
            setErrorState(window.translate('Goal cannot be 0'));
            return;
        }

        setRunningNewPlan(true);
        setGoalIndex(0);
    };

    const checkActiveQuotes = async () => {
        const data = await GetActiveQuotes(campaign.id, { campaignBurstId: selectedBurstId });
        if (data.count > 0) {
            addMessage({
                message: window.translate(
                    'There is currently an active quote for this campaign. Creating a new plan will expire this quote. Are you sure you wish to proceed?'
                ),
                buttons: [
                    {
                        text: window.translate('Cancel'),
                        style: { ...whiteButtonStyle, width: 87 },
                        callback: null
                    },
                    {
                        text: window.translate('Confirm'),
                        style: { cursor: 'pointer' },
                        callback: multiplePlanSelection ? checkCanPlan : planPackage
                    }
                ]
            });
        } else {
            multiplePlanSelection ? checkCanPlan() : planPackage();
        }
    };

    useEffect(() => {
        if (goalIndex >= 0 && runningNewPlan && mounted) {
            setCurrentGoalLabel(
                `Delivery Mode - ${goals[goalIndex]?.name} - Total Airtime Cost: ${formatCurrency(totalAirtimeCost)}`
            );
            updateGoal(goalIndex);
        }

        if (!runningNewPlan && mounted) {
            setIsReserved(true);
        }
    }, [runningNewPlan, goalIndex, totalAirtimeCost]);

    const checkCanPlan = () => {
        if (
            campaign.businessType !== BusinessTypes.AIRTIMEBURST.name &&
            (!campaign.dealVersionId || campaign.dealVersionId === window.emptyGuid)
        ) {
            setErrorState(window.translate('No Deal'));
            return;
        }
        if (!campaign.campaignStations.length && !isPackage) {
            setErrorState(window.translate('No Stations'));
            return;
        }
        if (!campaign.id || campaign.id === window.emptyGuid) {
            setErrorState(window.translate('Campaign not saved'));
            return;
        }
        if (!campaign.advertiserAccountId || campaign.advertiserAccountId === window.emptyGuid) {
            if (!campaign.agencyAccountId || campaign.agencyAccountId === window.emptyGuid) {
                setErrorState(window.translate('Campaign has no client or agency'));
                return;
            }
        }
        if (!campaign.id || campaign.id === window.emptyGuid) {
            setErrorState(window.translate('Campaign not saved'));
            return;
        }

        setGoalsMenuOpen(!goalsMenuOpen);
    };

    const getCampaignBurst = async () => {
        if (!selectedBurstId) {
            return;
        }
        try {
            const data = await GetCampaignBurst(selectedBurstId);
            if (mounted) {
                setSelectedBurst(data);
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(err.response?.data ?? err.messasge);
            addMessage({
                message: `${window.translate('Failed to load campaign burst.')} ${err.response?.status}`,
                buttons: [
                    { text: window.translate('Retry'), callback: getCampaignBurst, useSecondaryStyle: true },
                    { text: window.translate('Ok') }
                ]
            });
        }
    };

    const getCampaignSettings = async () => {
        try {
            const settings = await GetCampaignSettings();
            if (mounted) {
                setMultiplePlanSelection(settings.multiplePlanSelection);
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(err.response?.data ?? err.message ?? err);
        }
    };

    useEffect(() => {
        getCampaignBurst();
    }, [selectedBurstId, renderHack]);

    useEffect(() => {
        mounted = true;
        getCampaignSettings();
        return () => {
            mounted = false;
        };
    }, []);

    return (
        <div style={planManagerContainerStyle}>
            {!hasPlan || planning ? (
                <div
                    style={{
                        ...disabledStyle,
                        height: 65,
                        width: 398,
                        borderRadius: 10
                    }}
                />
            ) : (
                <div style={{ display: 'flex' }}>
                    <AsSelectWithPlaceholder
                        id='station-input'
                        name='station'
                        placeholderText={window.translate('All Station')}
                        placeholderId={window.emptyGuid}
                        title={window.translate('Performance by Station')}
                        selectStyleOverride={{ width: 274 }}
                        values={stations}
                        value={selectedStationId}
                        onChange={stationChange}
                        disabled={planning || (!isReserved && !lastPlanIsBooked)}
                    />

                    {modalShowing ? (
                        <DeliveryTableModal
                            onClose={handleClose}
                            onSelect={() => {
                                setModalShowing(false);
                            }}
                            tableColumnDefinitions={DeliveryTableColumnDefinitions}
                            selectedPlanId={selectedPlanId}
                            setSelectedStationId={setSelectedStationId}
                            setModalShowing={setModalShowing}
                            showImpacts={showImpacts}
                            showBaseImpacts={showBaseImpacts}
                        />
                    ) : null}
                    <AsButton
                        id='summarybtn'
                        style={{ ...planButtonStyle, margin: '18px 0px 0px 29px' }}
                        handleClick={handleOpen}
                        disabled={planning || (!isReserved && !lastPlanIsBooked)}
                    >
                        {window.translate('Summary')}
                    </AsButton>
                </div>
            )}
            {!hasPlan || planning ? (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center'
                    }}
                >
                    <div
                        style={{
                            ...disabledStyle,
                            height: 56,
                            width: 378,
                            borderRadius: 5
                        }}
                    />
                    <div
                        style={{
                            ...disabledStyle,
                            marginTop: 10,
                            height: 32,
                            width: 515
                        }}
                    />
                </div>
            ) : (
                <div
                    style={{
                        marginTop: 15.5,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center'
                    }}
                >
                    <span style={campaignGoalTitleStyle}>{window.translate(currentGoalLabel)}</span>
                    <div style={{ marginTop: 13 }}>
                        <span style={airtimeReservationTextStyle}>
                            {window.translate('Airtime reservation status : ')}
                        </span>
                        {lastPlanIsBooked ? (
                            <span style={reservedTextStyle('#199001')}>{window.translate('Airtime is booked.')}</span>
                        ) : isReserved ? (
                            <ReservationTimer
                                reservedUntil={reservedUntil}
                                stopTicking={runningNewPlan}
                                isMounted={mounted}
                                isReserved={isReserved}
                                setIsReserved={setIsReserved}
                                canQuote={canQuote}
                                planExpired={planExpired}
                                setPlanExpired={setPlanExpired}
                                runningNewPlan={runningNewPlan}
                                setRunningNewPlan={setRunningNewPlan}
                            ></ReservationTimer>
                        ) : (
                            <span style={reservedTextStyle(plan?.quote?.status < 4 ? '#199001' : '#DB0000')}>
                                {window.translate(
                                    plan?.quote?.statusName && plan?.quote?.statusName?.toLowerCase() !== 'reserved'
                                        ? 'Airtime is ' + plan?.quote?.statusName?.toLowerCase() + '.'
                                        : 'Airtime has expired.'
                                )}
                            </span>
                        )}
                    </div>
                </div>
            )}
            <div
                style={{
                    display: 'flex',
                    marginTop: 14.5,
                    marginRight: 25
                }}
            >
                <div style={{ width: 32 }} />
                {isPackage || !multiplePlanSelection ? (
                    <AsButton
                        id='planbtn'
                        handleClick={checkActiveQuotes}
                        style={planButtonStyle}
                        disabled={planning || readOnly}
                    >
                        <span>{window.translate('New Plan')}</span>
                    </AsButton>
                ) : (
                    <AsButton
                        id='planbtn'
                        handleClick={checkActiveQuotes}
                        style={planning || readOnly ? disabledPlanButtonStyle : planButtonStyle}
                        disabled={planning || readOnly}
                    >
                        <span>{window.translate('New Plan')}</span>
                        <span>
                            <IconChevronDown
                                height={19}
                                width={19}
                                style={{
                                    verticalAlign: 'bottom',
                                    marginLeft: 8,
                                    marginTop: 2
                                }}
                            />
                        </span>
                    </AsButton>
                )}
                {goalsMenuOpen && (
                    <div style={goalsContainerStyle}>
                        <PlanGoals goals={goals} updateGoal={goPlan} setReady={setReady} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default PlanManager;
